import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

/** DICOM LO limit — mirrored by the connector's rename validators. */
const MAX_LABEL_LENGTH = 64;

type Validity = 'valid' | 'empty' | 'tooLong' | 'invalidChars';

function classify(value: string): Validity {
    const trimmed = value.trim();
    if (trimmed === '') {
        return 'empty';
    }
    if (trimmed.length > MAX_LABEL_LENGTH) {
        return 'tooLong';
    }
    // Backslash is the DICOM multi-value delimiter; control
    // characters are invalid in an LO value.
    // eslint-disable-next-line no-control-regex
    if (/[\\\u0000-\u001f\u007f]/.test(trimmed)) {
        return 'invalidChars';
    }
    return 'valid';
}

const ERROR_MESSAGE_ID: Record<Exclude<Validity, 'valid'>, string> = {
    empty: 'renameDataset.errorEmpty',
    tooLong: 'renameDataset.errorTooLong',
    invalidChars: 'renameDataset.errorInvalidChars',
};

type Props = {
    open: boolean;
    /** Dialog title, already translated. */
    title: string;
    /** The current name, shown as the editable starting value. */
    initialValue: string;
    onCancel: () => void;
    /** Called with the trimmed new name; only ever a valid one. */
    onConfirm: (value: string) => void;
};

/**
 * Renames a dataset's display label (DICOM LO rules: non-empty, at most
 * 64 characters, no backslash or control characters). Invalid input
 * blocks the confirm — it is never silently corrected (only trimmed).
 */
export default function RenameDatasetDialog({
    open,
    title,
    initialValue,
    onCancel,
    onConfirm,
}: Props) {
    const intl = useIntl();
    const [value, setValue] = useState(initialValue);

    // Each opening edits the CURRENT name, not a previous session's text.
    useEffect(() => {
        if (open) {
            setValue(initialValue);
        }
    }, [open, initialValue]);

    const validity = classify(value);
    const canConfirm = validity === 'valid' && value.trim() !== initialValue;

    const confirm = (): void => {
        if (validity === 'valid') {
            onConfirm(value.trim());
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="xs"
            fullWidth
            PaperProps={{ elevation: 0 }}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    margin="dense"
                    size="small"
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && canConfirm) {
                            event.preventDefault();
                            confirm();
                        }
                    }}
                    label={intl.formatMessage({ id: 'renameDataset.label' })}
                    error={validity !== 'valid'}
                    helperText={
                        validity !== 'valid'
                            ? intl.formatMessage({
                                  id: ERROR_MESSAGE_ID[validity],
                              })
                            : ' '
                    }
                    inputProps={{ 'data-testid': 'rename-dataset-input' }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} color="inherit">
                    {intl.formatMessage({ id: 'cancel' })}
                </Button>
                <Button
                    onClick={confirm}
                    variant="contained"
                    disabled={!canConfirm}
                    data-testid="rename-dataset-confirm"
                >
                    {intl.formatMessage({ id: 'rename' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
